import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { UtmFields } from "@/lib/utm";

// Historico das UTMs geradas (tabela utm_links, RLS por tenant).

export interface UtmLinkRow {
  readonly id: string;
  readonly base_url: string;
  readonly utm_source: string;
  readonly utm_medium: string;
  readonly utm_campaign: string;
  readonly utm_term: string | null;
  readonly utm_content: string | null;
  readonly full_url: string;
  readonly created_at: string;
}

const UTM_HISTORY_KEY = ["utm-links"] as const;
const UTM_HISTORY_LIMIT = 20;

async function fetchUtmLinks(): Promise<UtmLinkRow[]> {
  const { data, error } = await supabase
    .from("utm_links")
    .select("id, base_url, utm_source, utm_medium, utm_campaign, utm_term, utm_content, full_url, created_at")
    .order("created_at", { ascending: false })
    .limit(UTM_HISTORY_LIMIT);
  if (error) throw error;
  return (data ?? []) as UtmLinkRow[];
}

export function useUtmLinks() {
  return useQuery<UtmLinkRow[], Error>({ queryKey: UTM_HISTORY_KEY, queryFn: fetchUtmLinks, staleTime: 30_000 });
}

export interface SaveUtmInput {
  readonly tenantId: string;
  readonly fields: UtmFields;
  readonly fullUrl: string;
}

export function useSaveUtmLink() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SaveUtmInput>({
    mutationFn: async ({ tenantId, fields, fullUrl }) => {
      const { error } = await supabase.from("utm_links").insert({
        tenant_id: tenantId,
        base_url: fields.url.trim(),
        utm_source: fields.source.trim(),
        utm_medium: fields.medium.trim(),
        utm_campaign: fields.campaign.trim(),
        utm_term: fields.term.trim() || null,
        utm_content: fields.content.trim() || null,
        full_url: fullUrl,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: UTM_HISTORY_KEY }),
  });
}
